import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { CaptionHeadingDescription } from '@/components/common/CaptionHeadingDescription';
import { Heading, HeadingLevel, Paragraph } from '@/components/common/MarkUp';
import { Section } from '@/components/layout/Section';
import { useJobListContext } from '@/components/page/careers/context/JobListContext';
import { JobFilters } from '@/components/page/careers/JobFilters';
import { JobPosting } from '@/components/page/careers/JobPosting';
import { NoResults } from '@/components/page/careers/NoResults';
import { bodyLarge, bodyMedium, subheadingSmall } from '@/styles/typography';

const openRolesHeading = [
  {
    _key: 'f3309458e69f',
    _type: 'block' as const,
    children: [
      {
        _key: '8e660726dd4d',
        _type: 'span',
        marks: [],
        text: 'Open ',
      },
      {
        _key: 'd9667a91d1c7',
        _type: 'span',
        marks: ['7091c9a16574'],
        text: 'roles',
      },
    ],
    markDefs: [
      {
        _key: '7091c9a16574',
        _type: 'highlightAnnotation',
        colour: {
          title: 'eclipse / eclipse-200',
          value: 'rgb(221, 252, 157)',
          token: '--decorative-eclipse',
        },
        style: 'straight',
      },
    ],
    style: 'normal',
  },
];

const JobList = () => {
  const { sortedJobs, filteredJobs, atsData } = useJobListContext();

  return (
    <Section backgroundColour="--background-warm-base" className="job-list">
      <JobListContainer id="job-list">
        <HeadingLevel>
          <JobListHeader>
            <CaptionHeadingDescription
              heading={openRolesHeading}
              description={
                'Join our Multiverse community and never stop growing.'
              }
              defaultHeadingSize="display-md"
              desktopAlignment="center"
              mobileAlignment="center"
              padding={{ description: 'small', descriptionMobile: 'small' }}
            />
          </JobListHeader>
          <JobFilters />
          {filteredJobs.length ? (
            <>
              <JobsNumHeading>
                <span data-testid="number-of-jobs">
                  {filteredJobs.length === atsData.jobPostings.length
                    ? 'All'
                    : `${filteredJobs.length}`}
                </span>{' '}
                jobs
              </JobsNumHeading>
              {sortedJobs.map(([department, jobs]) => {
                return (
                  <DepartmentContainer key={department}>
                    <HeadingLevel>
                      <DepHeading>{department}</DepHeading>
                    </HeadingLevel>
                    {jobs.map((job) => (
                      <JobPosting key={job.id} {...job} />
                    ))}
                  </DepartmentContainer>
                );
              })}
            </>
          ) : (
            <NoResults />
          )}
        </HeadingLevel>
      </JobListContainer>
    </Section>
  );
};

const JobListContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${screen.sm} {
    align-items: center;
    min-height: 100vh;
  }
`;

const JobListHeader = styled.div`
  margin: var(--spacing-xxxx-large) 0 var(--spacing-xx-large) 0;
  text-align: center;
  ${Paragraph} {
    ${bodyMedium}
  }
`;

const JobsNumHeading = styled.p`
  ${bodyLarge}
  letter-spacing: var(--letter-spacing-loose);
  margin-top: var(--spacing-large);
  margin-bottom: var(--spacing-large);

  :focus {
    outline: none;
  }

  ${screen.sm} {
    width: 626px;
  }

  ${screen.md} {
    width: 836px;
  }

  span {
    font-weight: var(--font-weight-semibold);
  }
`;

const DepartmentContainer = styled.div`
  margin-bottom: var(--spacing-x-large);
  color: var(--text-strong);
  max-width: 836px;
  width: 100%;
`;

const DepHeading = styled(Heading)`
  ${subheadingSmall}
  text-transform: capitalize;

  letter-spacing: var(--letter-spacing-loose);
  line-height: var(--line-height-loose);
`;

export { JobList };
